// OrganizationModal.tsx
import React, { useEffect, useMemo, useState } from "react";
import { authClient, useSession, API_URL } from "@/config/auth";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Loader2, Search, Building2, Users, MoreHorizontal, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { widgetService } from "@/api/services/widgetService";
import { HttpClient } from "@/api/services/HttpClient";
import { useOrganization } from '@/context/OrganizationContext';

/**
 * OrganizationModal
 *
 * Uses Better-Auth hooks:
 * - useSession() to find activeOrgId
 * - OrganizationContext to share active organization/member role data
 *
 * The modal supports:
 * - General tab (basic org info, leave/delete actions)
 * - Members tab (list, search, change role, remove, invite)
 *
 * Note: authentication/authorization is enforced by server. Frontend only provides UX.
 */

interface OrganizationModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

type Tab = "general" | "members";

const buildAuthHeaders = async () => {
    const token = await HttpClient.getAuthToken();
    const headers: Record<string, string> = {
        "Content-Type": "application/json",
    };

    if (token) {
        headers["Authorization"] = `Bearer ${token}`;
    }

    return headers;
};

export const OrganizationModal: React.FC<OrganizationModalProps> = ({ open, onOpenChange }) => {
    const { data: session } = useSession();
    const { activeOrg, orgPending, refetchOrg, activeMemberRole } = useOrganization();

    const { toast } = useToast();

    const [activeTab, setActiveTab] = useState<Tab>("general");

    // If there's no active org id, don't render modal
    const activeOrgId = session?.session?.activeOrganizationId ?? null;
    if (!activeOrgId) return null;

    // If modal is open but org is loading, show loader inside modal
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-[800px] p-0 overflow-hidden h-[600px] flex gap-0 bg-background">
                {/* TODO:  Add cross on right corner to close the dialog */}
                {/* Sidebar */}
                <div className="w-[240px] bg-muted/30 border-r p-4 flex flex-col gap-6">
                    <div>
                        <h2 className="font-semibold text-lg tracking-tight">Organization</h2>
                        <p className="text-xs text-muted-foreground">Manage your organization.</p>
                    </div>

                    <nav className="flex flex-col gap-1">
                        <NavButton
                            active={activeTab === "general"}
                            onClick={() => setActiveTab("general")}
                            icon={<Building2 className="w-4 h-4" />}
                            label="General"
                        />
                        <NavButton
                            active={activeTab === "members"}
                            onClick={() => setActiveTab("members")}
                            icon={<Users className="w-4 h-4" />}
                            label="Members"
                        />
                    </nav>
                </div>

                {/* Content Area */}
                <div className="flex-1 overflow-y-auto">
                    {orgPending && open ? (
                        <div className="p-8 flex items-center justify-center">
                            <Loader2 className="animate-spin h-5 w-5 mr-2" /> Loading organization...
                        </div>
                    ) : activeTab === "general" ? (
                        <GeneralTab org={activeOrg} refetchOrg={refetchOrg} onClose={() => onOpenChange(false)} />
                    ) : (
                        <MembersTab
                            orgId={activeOrg?.id}
                            open={open}
                            orgMembers={activeOrg?.members ?? []}
                            invitations={activeOrg?.invitations ?? []}
                            currentUserRole={activeMemberRole?.role ?? null}
                            refetchOrg={refetchOrg}
                        />
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

// -------------------- Subcomponents --------------------

const NavButton = ({ active, onClick, icon, label }: any) => (
    <Button
        variant={active ? "secondary" : "ghost"}
        className={`justify-start gap-3 h-9 font-normal ${active ? "bg-muted font-medium" : ""}`}
        onClick={onClick}
    >
        {icon}
        {label}
    </Button>
);

// ---------- General Tab ----------
// Slimmed GeneralTab that uploads logo via widgetService.uploadWidgetAsset
// and updates the org via authClient.organization.update(...) as requested.

const GeneralTab = ({ org, refetchOrg, onClose }: { org: any; refetchOrg?: () => Promise<void>; onClose: () => void }) => {
    const { toast } = useToast();

    // Edit modal state
    const [editOpen, setEditOpen] = useState(false);
    const [name, setName] = useState(org?.name ?? "");
    const [logoPreview, setLogoPreview] = useState<string | null>(org?.logo ?? null);
    const [logoFile, setLogoFile] = useState<File | null>(null);

    // action state
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<number | null>(null);

    useEffect(() => {
        if (!editOpen) {
            setName(org?.name ?? "");
            setLogoPreview(org?.logo ?? null);
            setLogoFile(null);
        }
    }, [org]);

    if (!org) return null;

    const handleLogoPick = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > 2 * 1024 * 1024) {
            toast({ title: "File too large", description: "Logo must be <= 2MB", variant: "destructive" });
            return;
        }
        if (!["image/png", "image/jpeg", "image/svg+xml"].includes(file.type)) {
            toast({ title: "Invalid file type", description: "Use PNG, JPEG or SVG", variant: "destructive" });
            return;
        }

        setLogoFile(file);
        setLogoPreview(URL.createObjectURL(file));
    };

    // Minimal progress wrapper for widgetService.uploadWidgetAsset if it supports progress events via window event.
    // If your upload function returns progress differently, adapt this.
    const uploadLogoAndGetUrl = async (file: File) => {
        setUploading(true);
        setUploadProgress(0);

        try {
            // widgetService.uploadWidgetAsset(file, token) => { fileUrl: string }
            const res = await widgetService.uploadWidgetAsset(file);

            // some uploaders return { fileUrl } or { data: { fileUrl } }, handle both
            const fileUrl = res;
            if (!fileUrl) throw new Error("No file URL returned from uploader");

            setUploadProgress(100);
            return fileUrl;
        } finally {
            setUploading(false);
            setTimeout(() => setUploadProgress(null), 400); // brief UI cleanup
        }
    };

    const handleSave = async () => {
        if (!name?.trim()) {
            toast({ title: "Name required", variant: "destructive" });
            return;
        }

        setSaving(true);
        try {
            let logoUrl = org.logo ?? null;

            if (logoFile) {
                // upload and get url
                logoUrl = await uploadLogoAndGetUrl(logoFile);
            }

            // call authClient.organization.update with the exact shape you provided
            if (!authClient?.organization?.update) {
                throw new Error("authClient.organization.update is not available");
            }

            await (authClient.organization.update as any)({
                data: {
                    name: name.trim(),
                    // do not change slug unless you want to. Keep same slug by default.
                    slug: org.slug,
                    logo: logoUrl,
                },
                organizationId: org.id,
            });

            toast({ title: "Organization updated" });
            setEditOpen(false);
            setLogoFile(null);
            if (refetchOrg) await refetchOrg();
        } catch (err) {
            console.error("Update failed", err);
            toast({ title: "Failed to update organization", variant: "destructive" });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-8 space-y-8">
            <div>
                <h3 className="text-lg font-medium mb-6">General</h3>

                <div className="flex items-center justify-between py-4">
                    <div className="flex items-center gap-4">
                        <Avatar className="h-12 w-12 rounded-lg border">
                            <AvatarImage src={org.logo} />
                            <AvatarFallback className="rounded-lg bg-primary/10 text-primary">
                                {org.name?.substring(0, 2).toUpperCase()}
                            </AvatarFallback>
                        </Avatar>
                        <div>
                            <p className="font-medium">{org.name}</p>
                            <p className="text-sm text-muted-foreground capitalize">{org.slug}</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
                            Update Organization Details
                        </Button>

                        {/* <Button variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50">
                            Delete
                        </Button> */}
                    </div>
                </div>

                <Separator className="my-6" />

                {/* <div className="space-y-6">
                    <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Leave organization</span>
                        <Button variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50">
                            Leave organization
                        </Button>
                    </div>
                </div> */}
            </div>

            {editOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/40 h-full w-full" onClick={() => setEditOpen(false)} />
                    <div className="bg-background p-6 rounded-lg z-10 w-[520px]">
                        <h4 className="font-medium mb-3">Update organization profile</h4>

                        <div className="space-y-4">
                            <div>
                                <label className="text-xs text-muted-foreground">Name</label>
                                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Organization name" />
                            </div>

                            <div>
                                <label className="text-xs text-muted-foreground">Logo</label>
                                <div className="flex items-center gap-3 mt-2">
                                    <div className="h-12 w-12 rounded-lg border flex items-center justify-center overflow-hidden">
                                        {logoPreview ? (
                                            // eslint-disable-next-line @next/next/no-img-element
                                            <img src={logoPreview} alt="preview" className="h-full w-full object-cover" />
                                        ) : (
                                            <div className="text-xs text-muted-foreground">No logo</div>
                                        )}
                                    </div>

                                    <div className="flex gap-2 items-center">
                                        <div
                                            onClick={() => document.getElementById("org-logo-input")?.click()}
                                        >
                                            <Button size="sm" variant="outline">Choose file</Button>
                                        </div>

                                        <input
                                            id="org-logo-input"
                                            type="file"
                                            className="hidden"
                                            accept="image/png,image/jpeg,image/svg+xml"
                                            onChange={handleLogoPick}
                                        />
                                        <Button size="sm" variant="ghost" onClick={() => { setLogoFile(null); setLogoPreview(org.logo ?? null); }}>
                                            Reset
                                        </Button>
                                    </div>
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">PNG, JPEG or SVG. Max 2MB.</p>
                            </div>

                            {uploadProgress !== null && (
                                <div>
                                    <div className="w-full bg-muted/30 h-2 rounded overflow-hidden">
                                        <div style={{ width: `${uploadProgress}%` }} className="h-2 bg-primary" />
                                    </div>
                                    <p className="text-xs mt-1">{uploadProgress}%</p>
                                </div>
                            )}

                            <div className="flex justify-end gap-2 pt-2">
                                <Button variant="ghost" onClick={() => setEditOpen(false)} disabled={saving || uploading}>
                                    Cancel
                                </Button>
                                <Button onClick={handleSave} disabled={saving || uploading}>
                                    {saving ? "Saving..." : "Save changes"}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};


// ---------- Members Tab ----------
const MembersTab = ({
    orgId,
    open,
    orgMembers,
    invitations,
    currentUserRole,
    refetchOrg,
}: {
    orgId?: string | null;
    open: boolean;
    orgMembers: any[];
    invitations: any[];
    currentUserRole?: string | null;
    refetchOrg?: () => Promise<void>;
}) => {
    const [members, setMembers] = useState<any[]>(orgMembers ?? []);
    const [invitationsState, setInvitationsState] = useState<any[]>(invitations ?? []);
    const [search, setSearch] = useState("");
    const { toast } = useToast();

    // tab state: "members" or "invitations"
    const [activeTab, setActiveTab] = useState<"members" | "invitations">("members");

    // Invite dialog state
    const [inviteOpen, setInviteOpen] = useState(false);
    const [inviteEmail, setInviteEmail] = useState("");
    const [inviteRole, setInviteRole] = useState("viewer");
    const [inviteLoading, setInviteLoading] = useState(false);

    const [busyMemberId, setBusyMemberId] = useState<string | null>(null);
    const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);
    const [cancellingInvitationId, setCancellingInvitationId] = useState<string | null>(null);

    // Sync props when they change
    useEffect(() => {
        setMembers(orgMembers ?? []);
    }, [orgMembers]);

    useEffect(() => {
        setInvitationsState(invitations ?? []);
    }, [invitations]);

    // filtered results for members
    const filteredMembers = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return members;
        return members.filter((m) => {
            const name = (m.user?.name ?? "").toLowerCase();
            const email = (m.user?.email ?? "").toLowerCase();
            return name.includes(q) || email.includes(q);
        });
    }, [members, search]);

    // filtered results for invitations
    const filteredInvitations = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return invitationsState;
        return invitationsState.filter((inv) => {
            const email = (inv.email ?? "").toLowerCase();
            return email.includes(q);
        });
    }, [invitationsState, search]);

    // Available role options (keep in sync with frontend roles object)
    const roleOptions = [
        { value: "admin", label: "Admin" },
        { value: "supervisor", label: "Supervisor" },
        { value: "agent", label: "Agent" },
        { value: "viewer", label: "Viewer" },
    ];

    // Invite handler
    const handleInvite = async () => {
        if (!inviteEmail) {
            toast({ title: "Email is required", variant: "destructive" });
            return;
        }
        setInviteLoading(true);
        try {
            if (authClient?.organization?.inviteMember) {
                await (authClient.organization.inviteMember as any)({
                    email: inviteEmail,
                    role: inviteRole,
                    organizationId: orgId,
                });
            } else {
                const headers = await buildAuthHeaders();
                await fetch(`${API_URL}/auth/organization/create-invitation`, {
                    method: "POST",
                    headers,
                    body: JSON.stringify({ email: inviteEmail, role: inviteRole, organizationId: orgId }),
                });
            }

            setInviteEmail("");
            setInviteRole("viewer");
            setInviteOpen(false);

            // Optionally refetch org to get updated invitations/members
            if (refetchOrg) await refetchOrg();

            toast({ title: "Invitation sent" });
        } catch (e) {
            console.error("Invite failed", e);
            toast({ title: "Failed to send invitation", variant: "destructive" });
        } finally {
            setInviteLoading(false);
        }
    };

    const handleRoleChange = async (memberId: string, newRole: string) => {
        setBusyMemberId(memberId);
        try {
            if (authClient?.organization?.updateMemberRole) {
                const response = await (authClient.organization.updateMemberRole as any)({ memberId, role: newRole });
                if (response.data) {
                    setMembers((prev) => prev.map((m) => (m.id === memberId ? { ...m, role: newRole } : m)));
                    toast({ title: "Role updated" });
                } else {
                    toast({ title: "Failed to update role", variant: "destructive" });
                }
            }
        } catch (e) {
            console.error("Failed update role", e);
            toast({ title: "Failed to update role", variant: "destructive" });
        } finally {
            setBusyMemberId(null);
        }
    };

    const handleRemoveMember = async (memberId: string) => {
        if (!confirm("Remove member?")) return;
        setRemovingMemberId(memberId);
        try {
            if (authClient?.organization?.removeMember) {
                await (authClient.organization.removeMember as any)({ memberId });
            } else {
                const headers = await buildAuthHeaders();
                await fetch(`${API_URL}/auth/organization/remove-member`, {
                    method: "POST",
                    headers,
                    body: JSON.stringify({ memberId }),
                });
            }

            setMembers((prev) => prev.filter((m) => m.id !== memberId));
            if (refetchOrg) await refetchOrg();
            toast({ title: "Member removed" });
        } catch (e) {
            console.error("Failed remove member", e);
            toast({ title: "Failed to remove member", variant: "destructive" });
        } finally {
            setRemovingMemberId(null);
        }
    };

    // Cancel invitation
    const handleCancelInvitation = async (invitationId: string) => {
        if (!confirm("Cancel this invitation?")) return;
        setCancellingInvitationId(invitationId);
        try {
            if (authClient?.organization?.cancelInvitation) {
                await (authClient.organization.cancelInvitation as any)({ invitationId });
            } else {
                // fallback API path (adjust to your backend if different)
                const headers = await buildAuthHeaders();
                await fetch(`${API_URL}/auth/organization/cancel-invitation`, {
                    method: "POST",
                    headers,
                    body: JSON.stringify({ invitationId }),
                });
            }

            setInvitationsState((prev) => prev.filter((inv) => inv.id !== invitationId));
            if (refetchOrg) await refetchOrg();
            toast({ title: "Invitation cancelled" });
        } catch (e) {
            console.error("Failed cancel invitation", e);
            toast({ title: "Failed to cancel invitation", variant: "destructive" });
        } finally {
            setCancellingInvitationId(null);
        }
    };

    return (
        <div className="p-8 h-full flex flex-col">
            <div className="mb-6">
                <h3 className="text-lg font-medium">Members</h3>
                <div className="flex items-center gap-4 mt-4">
                    <div className="flex gap-4 text-sm border-b w-full pb-0">
                        <div
                            onClick={() => setActiveTab("members")}
                            className={`pb-2 font-medium px-1 cursor-pointer ${activeTab === "members" ? "border-b-2 border-primary" : "text-muted-foreground hover:text-foreground"}`}
                        >
                            Members <Badge variant="secondary" className="ml-1 text-xs rounded-sm">{members.length}</Badge>
                        </div>
                        <div
                            onClick={() => setActiveTab("invitations")}
                            className={`pb-2 px-1 cursor-pointer ${activeTab === "invitations" ? "border-b-2 border-primary font-medium" : "text-muted-foreground hover:text-foreground"}`}
                        >
                            Invitations <Badge variant="secondary" className="ml-1 text-xs rounded-sm">{invitationsState?.length ?? 0}</Badge>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-between gap-4 mb-4">
                <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input placeholder="Search" className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
                <div className="flex items-center gap-2">
                    <Button onClick={() => setInviteOpen(true)} size="sm">Invite</Button>
                </div>
            </div>

            <div className="flex-1 overflow-auto -mx-8 px-8">
                <div className="space-y-1">
                    {/* Header row */}
                    <div className="grid grid-cols-12 text-xs font-medium text-muted-foreground px-2 pb-2">
                        {activeTab === "members" ? (
                            <>
                                <div className="col-span-6">User</div>
                                <div className="col-span-3">Joined</div>
                                <div className="col-span-2">Role</div>
                                <div className="col-span-1"></div>
                            </>
                        ) : (
                            <>
                                <div className="col-span-6">Email</div>
                                <div className="col-span-3">Invited</div>
                                <div className="col-span-2">Role</div>
                                <div className="col-span-1"></div>
                            </>
                        )}
                    </div>

                    {activeTab === "members" &&
                        filteredMembers.map((member) => (
                            <div key={member.id} className="grid grid-cols-12 items-center py-3 px-2 hover:bg-muted/50 rounded-lg transition-colors border-b last:border-0 border-border/50">
                                <div className="col-span-6 flex items-center gap-3">
                                    <Avatar className="h-8 w-8">
                                        <AvatarImage src={member.user?.image} />
                                        <AvatarFallback>{(member.user?.name ?? "U").charAt(0)}</AvatarFallback>
                                    </Avatar>
                                    <div className="flex flex-col">
                                        <span className="text-sm font-medium">{member.user?.name}</span>
                                        <span className="text-xs text-muted-foreground">{member.user?.email}</span>
                                    </div>
                                </div>

                                <div className="col-span-3 text-sm text-muted-foreground">
                                    {member.createdAt ? new Date(member.createdAt).toLocaleDateString() : ""}
                                </div>

                                <div className="col-span-2">
                                    <Select defaultValue={member.role} onValueChange={(val) => handleRoleChange(member.id, val)}>
                                        <SelectTrigger className="h-8 text-xs w-[100px]">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {roleOptions.map((r) => (
                                                <SelectItem key={r.value} value={r.value}>
                                                    {r.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="col-span-1 flex justify-end">
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button variant="ghost" size="icon" className="h-8 w-8">
                                                <MoreHorizontal className="h-4 w-4" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                            <DropdownMenuItem className="text-red-600 focus:text-red-600" onClick={() => handleRemoveMember(member.id)}>
                                                {removingMemberId === member.id ? "Removing..." : "Remove member"}
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </div>
                            </div>
                        ))}

                    {activeTab === "invitations" &&
                        filteredInvitations.map((inv) => (
                            <div key={inv.id} className="grid grid-cols-12 items-center py-3 px-2 hover:bg-muted/50 rounded-lg transition-colors border-b last:border-0 border-border/50">
                                <div className="col-span-6 flex items-center gap-3">
                                    <div className="flex flex-col">
                                        <span className="text-sm font-medium">{inv.email}</span>
                                        <span className="text-xs text-muted-foreground">{inv.sentByName ?? ""}</span>
                                    </div>
                                </div>

                                <div className="col-span-3 text-sm text-muted-foreground">
                                    {inv.createdAt ? new Date(inv.createdAt).toLocaleDateString() : ""}
                                </div>

                                <div className="col-span-2 text-sm">
                                    {inv.role ?? "-"}
                                </div>

                                <div className="col-span-1 flex justify-end">
                                    <Button size="sm" variant="ghost" onClick={() => handleCancelInvitation(inv.id)}>
                                        {cancellingInvitationId === inv.id ? "Cancelling..." : "Cancel"}
                                    </Button>
                                </div>
                            </div>
                        ))}
                </div>
            </div>

            {/* Invite dialog (basic inline modal) */}
            {inviteOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/40" onClick={() => setInviteOpen(false)} />
                    <div className="bg-background p-6 rounded-lg z-10 w-[420px]">
                        <h4 className="font-medium mb-3">Invite member</h4>
                        <div className="space-y-3">
                            <div>
                                <label className="text-xs text-muted-foreground">Email</label>
                                <Input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="user@example.com" />
                            </div>
                            <div>
                                <label className="text-xs text-muted-foreground">Role</label>
                                <Select defaultValue={inviteRole} onValueChange={(v) => setInviteRole(v)}>
                                    <SelectTrigger className="h-8 text-xs w-full">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {roleOptions.map((r) => (
                                            <SelectItem key={r.value} value={r.value}>
                                                {r.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="flex justify-end gap-2 pt-2">
                                <Button variant="ghost" onClick={() => setInviteOpen(false)} disabled={inviteLoading}>
                                    Cancel
                                </Button>
                                <Button onClick={handleInvite} disabled={inviteLoading}>
                                    {inviteLoading ? "Inviting..." : "Send invite"}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
